import { ComponentProps, useMemo } from 'react'
import { StyleSheet, useWindowDimensions, View } from 'react-native'
import Svg, { Rect, Path, Defs, Pattern } from 'react-native-svg'
import { DS } from '../../design-system'
import { formatGameScheduledAt, getMonthName } from '../../utils'

export type ScheduledGameCardProps = {
  name: string
  gameType: string
  /** Blind increase display string, already fully formatted (e.g. "5 min blinds", "No blind increase") */
  blindTime: string
  startingChips: string
  /** Turn duration display string (e.g. "30 sec") */
  turnDuration: string
  scheduledAt: Date
  hash: string
  hostedBy?: string
  isHost?: boolean
  onShareGame?: () => void
  onCopyGameCode?: () => void
}

const CalendarDay = ({ scheduledAt }: { scheduledAt: Date }) => {
  const day = scheduledAt.getDate()
  const month = getMonthName(scheduledAt)

  return (
    <DS.Frame style={styles.calendarContainer}>
      <DS.Frame alignItems="center" justifyContent="center" bg="Brand/Maroon" style={{ width: 48, height: 22 }}>
        <DS.Text variant="Caption/Bold" color="Global/White" textAlign="center" style={styles.month}>
          {month}
        </DS.Text>
      </DS.Frame>
      <DS.Frame flex={1} justifyContent="center">
        <DS.Text variant="Heading 3/Medium" textAlign="center">
          {day}
        </DS.Text>
      </DS.Frame>
    </DS.Frame>
  )
}

const DecorationSvgPattern = () => {
  return (
    <Svg width="100%" height="16">
      <Defs>
        <Pattern id="pattern" x="0" y="0" width={64} height={16} viewBox="0 0 64 16" patternUnits="userSpaceOnUse">
          <HeartPatternSvg />
        </Pattern>
      </Defs>
      <Rect x="0" y="0" width={'100%'} height={16} fill="url(#pattern)" />
    </Svg>
  )
}
const HeartPatternSvg = (props: ComponentProps<typeof Svg>) => {
  return (
    <Svg width={64} height={16} viewBox="0 0 64 16" fill="none" {...props}>
      <Rect y={0} width={16} height={16} fill="#7B043D" />
      <Path
        d="M7.98425 3.37672C8.49503 2.05085 9.82369 1.10336 11.3773 1.10336C13.3718 1.10336 14.927 3.12192 14.9939 5.02147C15.1277 8.82514 8.27764 14.6947 7.98577 14.8976C7.69237 14.6931 0.840812 8.82514 0.97459 5.02147C1.04148 3.12192 2.59665 1.10336 4.59115 1.10336C6.1448 1.10336 7.47346 2.05085 7.98425 3.37672Z"
        fill="#FF3ED4"
      />
      <Rect x={32} width={16} height={16} fill="#7B043D" />
      <Path
        d="M39.9979 3.37658C40.5087 2.05071 41.8374 1.10323 43.391 1.10323C45.3855 1.10323 46.9407 3.12178 47.0076 5.02133C47.1414 8.825 40.2913 14.6945 39.9994 14.8975C39.706 14.693 32.8545 8.825 32.9883 5.02133C33.0552 3.12178 34.6103 1.10323 36.6048 1.10323C38.1585 1.10323 39.4871 2.05071 39.9979 3.37658Z"
        fill="#FF3ED4"
      />
      <Rect width={16} height={16} transform="matrix(1 0 0 -1 48 16)" fill="#FF3ED4" />
      <Path
        d="M56.0076 12.6234C56.5184 13.9493 57.8471 14.8968 59.4007 14.8968C61.3952 14.8968 62.9504 12.8782 63.0173 10.9787C63.1511 7.175 56.301 1.30547 56.0092 1.10254C55.7158 1.30699 48.8642 7.175 48.998 10.9787C49.0649 12.8782 50.62 14.8968 52.6145 14.8968C54.1682 14.8968 55.4968 13.9493 56.0076 12.6234Z"
        fill="#7B043D"
      />
      <Rect width={16} height={16} transform="matrix(1 0 0 -1 16 16)" fill="#FF3ED4" />
      <Path
        d="M23.994 12.6234C24.5048 13.9493 25.8335 14.8968 27.3871 14.8968C29.3816 14.8968 30.9368 12.8782 31.0037 10.9787C31.1374 7.175 24.2874 1.30547 23.9955 1.10254C23.7021 1.30699 16.8506 7.175 16.9844 10.9787C17.0512 12.8782 18.6064 14.8968 20.6009 14.8968C22.1546 14.8968 23.4832 13.9493 23.994 12.6234Z"
        fill="#7B043D"
      />
    </Svg>
  )
}

export const ScheduledGameCard = ({
  name,
  gameType,
  blindTime,
  startingChips,
  turnDuration,
  scheduledAt,
  hostedBy,
  hash,
  onShareGame,
  onCopyGameCode
}: ScheduledGameCardProps) => {
  const { height } = useWindowDimensions()
  const isSmallDevice = useMemo(() => height < 730 && onShareGame, [height, onShareGame])

  return (
    <DS.Frame bg="Global/White">
      <DecorationSvgPattern />
      <DS.Frame style={{ paddingHorizontal: 32, paddingVertical: isSmallDevice ? 16 : 32 }} gap={'12'}>
        <DS.Text variant="Heading 2/Medium">{name}</DS.Text>
        {hostedBy ? (
          <DS.Text variant="Body Small/Regular">
            Hosted by <DS.Text variant="Body Small/Medium">{hostedBy}</DS.Text>
          </DS.Text>
        ) : null}
        <DS.Frame style={styles.line} />
        <DS.Frame row>
          <DS.Frame flex={1} gap={isSmallDevice ? '12' : '16'}>
            <DS.Frame row gap={'8'} alignItems="center">
              <DS.Icon name="Switches" size={16} color="Global/Black" />
              <DS.Text variant="Body Medium/Regular" style={{ textTransform: 'capitalize' }}>
                {gameType}
              </DS.Text>
            </DS.Frame>

            <DS.Frame row gap={'8'} alignItems="center">
              <DS.Icon name="Arrow---right" size={16} color="Global/Black" />
              <DS.Text variant="Body Medium/Regular">{blindTime}</DS.Text>
            </DS.Frame>

            <DS.Frame row gap={'8'} alignItems="center">
              <DS.Icon name="Chip" size={16} color="Global/Black" />
              <DS.Text variant="Body Medium/Regular">{startingChips}</DS.Text>
            </DS.Frame>

            <DS.Frame row gap={'8'} alignItems="center">
              <DS.Icon name="Clock" size={16} color="Global/Black" />
              <DS.Text variant="Body Medium/Regular">{turnDuration}</DS.Text>
            </DS.Frame>

            <DS.Frame row gap={'8'}>
              <DS.Icon name="Calendar" size={16} style={{ marginTop: 2 }} />
              <DS.Frame gap={'4'}>
                <DS.Text variant="Body Medium/Medium">Scheduled Game</DS.Text>
                <DS.Text variant="Body Medium/Regular">{formatGameScheduledAt(scheduledAt)}</DS.Text>
              </DS.Frame>
            </DS.Frame>
          </DS.Frame>
          <CalendarDay scheduledAt={scheduledAt} />
        </DS.Frame>

        <DS.Frame gap={'6'}>
          {onShareGame ? (
            <DS.Button variant="Large/Primary" type="On Light" onPress={onShareGame}>
              Share Game
            </DS.Button>
          ) : null}

          {onCopyGameCode ? (
            <DS.Button
              variant="Large/Secondary"
              type="On Dark"
              onPress={onCopyGameCode}
              rightIcon={<DS.Icon name="Copy" size={16} color="Global/Black" />}
            >
              Copy game code:{' '}
              <DS.Text variant="Body Medium/Medium" style={{ textTransform: 'uppercase' }}>
                {hash}
              </DS.Text>
            </DS.Button>
          ) : null}
        </DS.Frame>
      </DS.Frame>
      <View style={styles.decorationInverted}>
        <DecorationSvgPattern />
      </View>
    </DS.Frame>
  )
}

const styles = StyleSheet.create({
  decorationInverted: {
    transform: [
      {
        rotate: '180deg'
      }
    ]
  },
  line: {
    height: 1,
    backgroundColor: DS.palette['Brand/Light Grey']
  },
  calendarContainer: {
    width: 48,
    height: 58,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Maroon']
  },
  month: {
    textTransform: 'uppercase'
  }
})
